import { useAuthFetch } from "@p0security/frontend-core";
import type {
  AgentActivityEntry,
  AgenticActivityFilters,
  AgenticActivityResponse,
  AgenticActivityUnavailableGateway,
} from "@p0security/integrations-agentic-shared/interface";
import type { CollectExportData } from "components/Export";
import { useCallback, useRef, useState } from "react";

import type { TimeRange } from "../timeRange.ts";
import { DEFAULT_TIME_RANGE, resolveTimeRange } from "../timeRange.ts";

/** Whether a log provider is configured, discriminated at the type level
 * rather than via a `boolean | undefined` that collapses "not configured"
 * and "not loaded yet" into the same falsy value. */
export type LogProviderStatus = "configured" | "loading" | "unconfigured";

export const DEFAULT_PAGE_SIZE = 50;
/** Page size options surfaced to users for the on-screen table, independent
 * of the export flow's own fixed page size below. */
export const PAGE_SIZE_OPTIONS = [50, 100] as const;
/** The backend's maximum activity page size, so a full export costs as few
 * round trips as the API allows. */
const EXPORT_PAGE_SIZE = 1000;
/** Caps a single export, so a busy tenant can't walk its log provider's
 * pages indefinitely. Surfaced to users in the export dialog. */
export const EXPORT_MAX_ENTRIES = 10000;

const eventLogErrorMessage = (status: number) => {
  if (status === 400) return "Check the event filters and try again.";
  if (status === 401) return "Your session has expired. Sign in and try again.";
  if (status === 403) {
    return "You do not have permission to view the agentic event log.";
  }
  return "The agentic event log could not be loaded. Try again.";
};

const fetchErrorMessage = (error: string | undefined) =>
  error === "Unauthorized"
    ? "Your session has expired. Sign in and try again."
    : error;

const eventQuery = (
  filters: AgenticActivityFilters,
  { pageSize, pageToken }: { pageSize: number; pageToken?: string }
) => {
  const params = new URLSearchParams({ pageSize: pageSize.toString() });
  if (pageToken) params.set("pageToken", pageToken);
  for (const [key, value] of Object.entries(filters)) {
    if (value) params.set(key, value);
  }
  return params.toString();
};

/** Loads filtered agentic events for the active tenant. */
export function useEventLog(
  filters: AgenticActivityFilters,
  timeRange: TimeRange = DEFAULT_TIME_RANGE
) {
  const [entries, setEntries] = useState<AgentActivityEntry[]>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [logProviderConfigured, setLogProviderConfigured] = useState<boolean>();
  const [nextPageToken, setNextPageToken] = useState<string>();
  const [unavailableGateways, setUnavailableGateways] = useState<
    AgenticActivityUnavailableGateway[]
  >([]);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const handleFetchError = useCallback(
    (nextError: string | undefined) => setError(fetchErrorMessage(nextError)),
    []
  );
  const authFetch = useAuthFetch(handleFetchError, setLoading);
  const activeFiltersRef = useRef<AgenticActivityFilters>();
  // Resolved per request so a relative range keeps rolling.
  const resolveFilters = useCallback(
    () => ({ ...filters, ...resolveTimeRange(timeRange) }),
    [filters, timeRange]
  );
  const requestIdRef = useRef(0);

  const fetchPage = useCallback(
    async (
      pageToken: string | undefined,
      append: boolean,
      requestFilters: AgenticActivityFilters,
      requestPageSize: number
    ) => {
      const requestId = ++requestIdRef.current;
      const query = eventQuery(requestFilters, {
        pageSize: requestPageSize,
        pageToken,
      });

      if (!append) setError(undefined);
      const response = await authFetch(`agentic/activity?${query}`, {
        method: "GET",
        onNotOk: ({ status }) => {
          if (requestId === requestIdRef.current) {
            setError(eventLogErrorMessage(status));
          }
        },
      });
      if (requestId !== requestIdRef.current) return;
      if (!response) {
        setError(
          (previousError) =>
            previousError ??
            "The agentic event log could not be loaded. Try again."
        );
        return;
      }

      try {
        const payload: AgenticActivityResponse = await response.json();
        if (requestId !== requestIdRef.current) return;
        setEntries((previousEntries) =>
          append
            ? [...(previousEntries ?? []), ...payload.entries]
            : payload.entries
        );
        setNextPageToken(payload.nextPageToken);
        setLogProviderConfigured(payload.logProviderConfigured);
        setUnavailableGateways(payload.unavailableGateways ?? []);
      } catch {
        if (requestId === requestIdRef.current) {
          setError("The agentic event log response could not be read.");
        }
      }
    },
    [authFetch]
  );

  const loadFirstPage = useCallback(
    (requestPageSize: number) => {
      const nextFilters = resolveFilters();
      activeFiltersRef.current = nextFilters;
      setEntries(undefined);
      setNextPageToken(undefined);
      setLogProviderConfigured(undefined);
      setUnavailableGateways([]);

      return fetchPage(undefined, false, nextFilters, requestPageSize);
    },
    [fetchPage, resolveFilters]
  );

  const refresh = useCallback(
    () => loadFirstPage(pageSize),
    [loadFirstPage, pageSize]
  );

  // Applies the new page size immediately, rather than waiting for the next
  // refresh, so a mid-scroll selector change can't leave "load more" pulling
  // pages sized to a stale selection.
  const changePageSize = useCallback(
    (nextPageSize: number) => {
      setPageSize(nextPageSize);
      return loadFirstPage(nextPageSize);
    },
    [loadFirstPage]
  );

  const loadMore = useCallback(() => {
    if (!nextPageToken) return Promise.resolve();
    const nextFilters = activeFiltersRef.current ?? resolveFilters();
    return fetchPage(nextPageToken, true, nextFilters, pageSize);
  }, [fetchPage, nextPageToken, pageSize, resolveFilters]);

  // Exports read the same filtered query the table shows, but page far deeper
  // than the table does. Fetched with its own error handling so a failed
  // export reports as a toast instead of replacing the loaded table with an
  // error banner.
  const exportFetch = useAuthFetch();
  const collect: CollectExportData<AgentActivityEntry> = useCallback(
    async (onProgress, cancellation) => {
      const collectFilters = activeFiltersRef.current ?? resolveFilters();
      const collected: AgentActivityEntry[] = [];
      let pageToken: string | undefined = undefined;
      do {
        if (cancellation.current) return collected;
        const query = eventQuery(collectFilters, {
          pageSize: EXPORT_PAGE_SIZE,
          pageToken,
        });
        const response = await exportFetch(`agentic/activity?${query}`, {
          method: "GET",
        });
        // Cancellation may have been requested while the fetch was in
        // flight; don't process (or count toward the cap) a page that
        // arrives after the user already asked to stop.
        if (cancellation.current) return collected;
        // Partial data would export as if it were the complete event set,
        // so a failed page fails the whole export.
        if (!response) throw new Error("Failed to read the agentic event log");
        const payload: AgenticActivityResponse = await response.json();
        if (payload.unavailableGateways?.length) {
          throw new Error(
            "Some gateways' activity could not be read; try the export again"
          );
        }
        collected.push(...payload.entries);
        onProgress(
          `${collected.length} tool calls collected`,
          Math.min(collected.length / EXPORT_MAX_ENTRIES, 1)
        );
        // A log provider may return an empty page while more pages remain,
        // so pagination ends on a missing token rather than on an empty page.
        pageToken = payload.nextPageToken;
      } while (pageToken && collected.length < EXPORT_MAX_ENTRIES);
      return collected.slice(0, EXPORT_MAX_ENTRIES);
    },
    [exportFetch, resolveFilters]
  );

  // `entries` and `logProviderConfigured` are set together in `fetchPage` and
  // cleared together in `loadFirstPage`, so folding them into one status
  // keeps "not loaded yet" from being confused with "loaded and
  // unconfigured" at call sites (both would otherwise read as falsy).
  const logProviderStatus: LogProviderStatus =
    entries === undefined || logProviderConfigured === undefined
      ? "loading"
      : logProviderConfigured
        ? "configured"
        : "unconfigured";

  return {
    changePageSize,
    collect,
    entries,
    error,
    loadMore,
    loading,
    logProviderStatus,
    nextPageToken,
    pageSize,
    refresh,
    unavailableGateways,
  };
}
